import React from 'react';
import { Artwork } from '../../types/Artwork';
import { MEDIUM_DISPLAY } from '../../types/Medium';
import { YEAR_DISPLAY } from '../../types/Year';

interface ArtworkImageProps {
    artwork: Artwork;
    /** contain = letterbox within the box (cards); cover = fill+crop (landing hero); fill = stretch to
     *  the element box — use only when the box already matches the image aspect (e.g. the viewing
     *  room's max-h/max-w sizing) so it fills edge-to-edge with no distortion or letterbox seam. */
    fit: 'contain' | 'cover' | 'fill';
    /** Default lazy; ignored when `priority`. */
    loading?: 'lazy' | 'eager';
    /** Above-the-fold hero: eager + fetchpriority high. */
    priority?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /** Override the generated alt; pass '' for a decorative image. */
    alt?: string;
}

/** Descriptive alt text derived from the artwork's metadata (display strings). */
export const buildAlt = (a: Artwork): string =>
    a.alt ?? `${a.title} — ${MEDIUM_DISPLAY[a.medium]}, ${YEAR_DISPLAY[a.year]}, by Dragan Strukelj`;

// Shared responsive image: WebP-first <picture>, lazy by default, with an aspect-ratio
// hint (when known) to reduce CLS. Used by cards, the viewing room, and the landing hero.
export const ArtworkImage = ({ artwork, fit, loading = 'lazy', priority = false, className = '', style, alt }: ArtworkImageProps) => {
    const altText = alt ?? buildAlt(artwork);
    const fitClass = fit === 'cover' ? 'object-cover' : fit === 'fill' ? 'object-fill' : 'object-contain';
    const ratioStyle = artwork.aspectRatio ? { aspectRatio: String(artwork.aspectRatio), ...style } : style;
    // fetchpriority isn't in the installed React DOM types yet — apply via a typed-any spread.
    const priorityAttrs = priority ? ({ fetchpriority: 'high' } as any) : {};

    // `contents` removes the <picture>'s own box so the <img> is the direct flex/grid child of
    // the consumer's layout — the img's size constraints (e.g. max-w-[80%] in the viewing room,
    // h-full w-full in cards) then resolve against that container and center correctly. WebP
    // source selection is unaffected by CSS display.
    return (
        <picture className="contents">
            {artwork.srcWebp && <source srcSet={artwork.srcWebp} type="image/webp" />}
            <img
                src={artwork.src}
                alt={altText}
                loading={priority ? 'eager' : loading}
                decoding="async"
                className={`${fitClass} ${className}`}
                style={ratioStyle}
                {...priorityAttrs}
            />
        </picture>
    );
};
